import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { api, type Attention, type Overview, type OverviewEnv } from "../api";
import { HealthMark, city, relative, whatRuns } from "../components/ui";

// The question: what is running in the lab, and what needs someone now?

const ATTENTION_WORDS: Record<Attention["kind"], string> = {
  down: "Down",
  expired: "Expired",
  approval: "Waiting for approval",
  expiring: "Expiring",
  stopped: "Stopped",
};
const ATTENTION_PILL: Record<Attention["kind"], string> = {
  down: "negative",
  expired: "negative",
  approval: "warning",
  expiring: "warning",
  stopped: "",
};

function finding(o: Overview): string {
  const { up, environments, stopped, down, partlyUp } = o.totals;
  const parts = [`${up} of ${environments} environments running`];
  if (down + partlyUp) parts.push(`${down + partlyUp} down`);
  if (stopped) {
    const stoppedEnvs = o.environments.filter((e) => e.health === "stopped");
    const projects = [...new Set(stoppedEnvs.map((e) => e.projectName.split(" — ")[0]))];
    parts.push(`${stopped} stopped (${projects.join(", ")}), their disks still billed`);
  }
  return parts.join(" · ");
}

function Where({ envs }: { envs: OverviewEnv[] }) {
  const byCity = new Map<string, OverviewEnv[]>();
  for (const e of envs) byCity.set(city(e.region), [...(byCity.get(city(e.region)) ?? []), e]);
  const cities = [...byCity.entries()].sort((a, b) => b[1].length - a[1].length);
  return (
    <section aria-labelledby="where-title">
      <div style={{ marginBottom: 12 }}>
        <h2 id="where-title">Where it runs</h2>
        <p className="stamp" style={{ marginTop: 2 }}>One block per environment: filled running, outlined stopped.</p>
      </div>
      <div className="where" style={{ ["--cities" as string]: 1 }}>
        {cities.map(([name, list]) => {
          const running = list.filter((e) => e.health === "up").length;
          return (
            <div className="city" key={name}>
              <div className="city-name">
                <strong>{name}</strong>
                <span className="num">{running} of {list.length} running</span>
              </div>
              <div className="blocks" aria-hidden="true">
                {list.map((e) => (
                  <span key={e.id} className={`block ${e.health === "up" ? "" : e.health === "partly-up" ? "down" : e.health}`} title={`${e.projectName} · ${e.name}`} />
                ))}
              </div>
              <div className="city-list">{[...new Set(list.map((e) => e.project))].join(" · ")}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function OverviewPage() {
  const nav = useNavigate();
  const [all, setAll] = useState(false);
  const q = useQuery({ queryKey: ["overview"], queryFn: () => api<Overview>("/overview"), refetchInterval: 60_000 });

  if (q.isPending) {
    // The final layout with each value a flat grey bar at its real size; nothing moves when data lands.
    const bar = (w: number, h = 14) => <span className="skeleton" style={{ width: w, height: h }} />;
    return (
      <div className="canvas" aria-busy="true">
        <div className="title-row"><div><h1>Overview</h1><p className="sub">{bar(420, 16)}</p></div></div>
        <div className="kpis">
          {["Projects", "Environments running", "Boxes running", "Stopped"].map((l) => (
            <div className="kpi" key={l}><div className="kpi-label">{l}</div><div className="kpi-value">{bar(48, 28)}</div><div className="kpi-note">{bar(160, 12)}</div></div>
          ))}
        </div>
        <div className="split">
          <section className="panel flush"><div className="panel-head"><h2>Environments</h2></div>
            <table><tbody>{[0, 1, 2, 3, 4, 5].map((i) => <tr key={i}><td>{bar(220)}</td><td>{bar(70)}</td><td>{bar(60)}</td><td>{bar(140)}</td></tr>)}</tbody></table>
          </section>
          <section className="panel flush"><div className="panel-head"><h2>Needs attention</h2></div>
            <div className="list">{[0, 1, 2].map((i) => <div className="item" key={i}><div>{bar(200)}</div>{bar(60, 20)}</div>)}</div>
          </section>
        </div>
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="canvas">
        <div className="title-row"><h1>Overview</h1></div>
        <div className="panel">Couldn't load the lab's state: {q.error.message}. <button className="btn ghost" onClick={() => q.refetch()}>Retry</button></div>
      </div>
    );
  }
  const o = q.data;
  const envs = [...o.environments].sort(
    (a, b) => (a.health === "up" ? 1 : 0) - (b.health === "up" ? 1 : 0) || a.projectName.localeCompare(b.projectName) || a.name.localeCompare(b.name),
  );

  return (
    <div className="canvas">
      <div className="title-row">
        <div>
          <h1>Overview</h1>
          <p className="sub">{finding(o)}.</p>
        </div>
        <div className="actions">
          <span className="stamp">Checked {relative(o.checkedAt)}</span>
          <button className="btn" onClick={() => q.refetch()} disabled={q.isFetching}>{q.isFetching ? "Checking…" : "Refresh"}</button>
          <Link className="btn primary" to="/new">New project</Link>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi"><div className="kpi-label">Projects</div><div className="kpi-value num">{o.totals.projects}</div><div className="kpi-note">{o.byStage.released ?? 0} released · {o.byStage.pilot ?? 0} pilot · {o.byStage.build ?? 0} in build</div></div>
        <div className="kpi"><div className="kpi-label">Environments running</div><div className="kpi-value num">{o.totals.up}<span className="unit">of {o.totals.environments}</span></div><div className="kpi-note">{o.totals.down + o.totals.partlyUp} down</div></div>
        <div className="kpi"><div className="kpi-label">Boxes running</div><div className="kpi-value num">{o.totals.boxes}</div><div className="kpi-note">EC2 instances behind lab environments</div></div>
        <div className="kpi"><div className="kpi-label">Stopped</div><div className="kpi-value num">{o.totals.stopped}</div><div className="kpi-note">disks still billed until removed</div></div>
      </div>

      <div className="split">
        <section className="panel flush" aria-labelledby="envs-title">
          <div className="panel-head">
            <h2 id="envs-title">Environments</h2>
            <Link to="/projects" className="small">All projects</Link>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Project · environment</th><th>Status</th><th>On the lab</th><th className="hide-phone">Where</th><th className="hide-phone">What runs</th></tr>
              </thead>
              <tbody>
                {(all ? envs : envs.slice(0, 8)).map((e) => (
                  <tr key={e.id} className="link" onClick={() => nav(`/p/${e.project}`)}>
                    <td>
                      <Link to={`/p/${e.project}`} className="cell-main">{e.projectName.split(" — ")[0]}</Link>
                      <span className="muted"> · {e.name}</span>
                      <div className="cell-sub">{e.client || e.projectName.split(" — ")[1] || ""}</div>
                    </td>
                    <td><HealthMark health={e.health} reasons={e.healthReasons} /></td>
                    <td>{e.onLab ? <span className="pill positive">On the lab</span> : <span className="muted">Not yet</span>}</td>
                    <td className="hide-phone">{e.onLab ? "Lab host" : city(e.region)}</td>
                    <td className="hide-phone mono truncate" style={{ maxWidth: 220 }}>{whatRuns(e)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {envs.length > 8 && (
            <div className="item" style={{ display: "flex", justifyContent: "center" }}>
              <button className="btn ghost" onClick={() => setAll(!all)}>{all ? "Show the first 8" : `Show all ${envs.length} environments`}</button>
            </div>
          )}
        </section>

        <div style={{ display: "grid", gap: 24, alignContent: "start" }}>
        <section className="panel flush" aria-labelledby="attention-title">
          <div className="panel-head">
            <h2 id="attention-title">Needs attention</h2>
            <span className="stamp num">{o.attention.length}</span>
          </div>
          <div className="list">
            {o.attention.length === 0 && <div className="item"><span className="muted">Nothing needs anyone right now.</span></div>}
            {o.attention.map((a, i) => (
              <div className="item" key={`${a.project}-${a.env}-${a.kind}-${i}`}>
                <div>
                  <Link to={`/p/${a.project}`} className="cell-main">{a.projectName.split(" — ")[0]} · {a.env}</Link>
                  <div className="cell-sub">{a.text}</div>
                </div>
                <span className={`pill ${ATTENTION_PILL[a.kind]}`}>{ATTENTION_WORDS[a.kind]}</span>
              </div>
            ))}
          </div>
        </section>
        <Where envs={o.environments} />
        </div>
      </div>
    </div>
  );
}
